import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, fmtDate, fmtUSD, ROLES } from "@/lib/api";
import { getRoleTheme } from "@/lib/roleTheme";
import {
  Users, ShieldCheck, Briefcase, Building2, CheckCircle2, X, Trash2,
  MessageSquare, AlertTriangle, Search, Calendar, RefreshCw, ChevronLeft,
  Activity, Globe2,
} from "lucide-react";
import { toast } from "sonner";

const TAB_ROUTES = {
  overview: "/dashboard",
  users: "/admin/users",
  providers: "/admin/providers",
  properties: "/admin/properties",
  offers: "/admin/offers",
  conversations: "/admin/conversations",
  appointments: "/admin/appointments",
  audit: "/admin/audit",
  danger: "/admin/danger",
};
const ROUTE_TO_TAB = Object.fromEntries(Object.entries(TAB_ROUTES).map(([k, v]) => [v, k]));

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = ROUTE_TO_TAB[location.pathname] || "overview";
  const setTab = (t) => navigate(TAB_ROUTES[t] || "/dashboard");

  const [stats, setStats] = useState(null);
  const [userActivity, setUserActivity] = useState(null); // user_id for modal
  const refreshStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  }, []);
  useEffect(() => { refreshStats(); }, [refreshStats]);

  // expose setUserActivity globally to children
  React.useEffect(() => { window.__adminOpenActivity = setUserActivity; return () => { delete window.__adminOpenActivity; }; }, []);

  return (
    <DashboardLayout>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Operations Console</h1>
          <p className="text-sm text-[var(--adn-text-soft)] mt-1">Full read/write across every record in the marketplace.</p>
        </div>
        <button data-testid="admin-refresh" onClick={refreshStats} className="btn-outline-navy px-4 py-2 rounded text-xs inline-flex items-center gap-2">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Users" v={stats?.users ?? "—"} />
        <StatCard icon={ShieldCheck} label="Approved providers" v={stats?.providers_approved ?? "—"} />
        <StatCard icon={Building2} label="Active properties" v={stats?.active_properties ?? "—"} />
        <StatCard icon={Briefcase} label="Offers" v={stats?.offers ?? "—"} accent />
      </div>

      <div className="mt-10 flex gap-2 text-sm overflow-x-auto pb-2">
        {[
          ["overview","Overview"],
          ["users","Users"],
          ["providers","Pending Providers"],
          ["properties","Properties"],
          ["offers","Offers"],
          ["conversations","Conversations"],
          ["appointments","Appointments"],
          ["audit","Activity Log"],
          ["danger","Danger Zone"],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-4 py-2 border shrink-0 whitespace-nowrap ${
              tab === id
                ? id === "danger" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-[var(--adn-gold)] bg-white"
                : "border-[var(--adn-line)] bg-white text-[var(--adn-text-soft)] hover:border-[var(--adn-navy)]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && <OverviewPanel stats={stats} />}
        {tab === "users" && <UsersPanel onChange={refreshStats} />}
        {tab === "providers" && <PendingProvidersPanel onChange={refreshStats} />}
        {tab === "properties" && <PropertiesPanel onChange={refreshStats} />}
        {tab === "offers" && <OffersPanel onChange={refreshStats} />}
        {tab === "conversations" && <ConversationsPanel onChange={refreshStats} />}
        {tab === "appointments" && <AppointmentsPanel onChange={refreshStats} />}
        {tab === "audit" && <AuditLogPanel />}
        {tab === "danger" && <DangerZonePanel onChange={refreshStats} />}
      </div>
      {userActivity && <UserActivityModal userId={userActivity} onClose={() => setUserActivity(null)} />}
    </DashboardLayout>
  );
}

/* ============== Panels ============== */

function OverviewPanel({ stats }) {
  const items = [
    ["Sellers", stats?.sellers],
    ["Investors", stats?.investors],
    ["Providers pending", stats?.providers_pending],
    ["Properties", stats?.properties],
    ["Accepted offers", stats?.accepted_offers],
    ["Threads", stats?.threads],
    ["Messages", stats?.messages],
    ["Appointments", stats?.appointments],
  ];
  return (
    <div className="bg-white border border-[var(--adn-line)] p-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(([k, v]) => (
        <div key={k} className="border border-[var(--adn-line)] p-4">
          <div className="text-[10px] uppercase tracking-wider text-[var(--adn-text-muted)]">{k}</div>
          <div className="font-display text-3xl tabular mt-1 font-light">{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

function UsersPanel({ onChange }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch {} finally { setBusy(false); }
  }, [q, role]);
  useEffect(() => { load(); }, [load]);

  const toggleApprove = async (u) => {
    try { await api.post("/admin/approve-provider", { user_id: u.user_id, approved: !u.is_approved }); toast.success("Updated"); load(); onChange(); } catch { toast.error("Failed"); }
  };
  const changeRole = async (u, newRole) => {
    try { await api.put(`/admin/users/${u.user_id}`, { role: newRole }); toast.success("Role updated"); load(); } catch { toast.error("Failed"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cascades — properties, offers, messages, appointments will be removed. This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("User deleted"); load(); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-[var(--adn-line)]">
      <div className="p-4 border-b border-[var(--adn-line)] flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search size={14} className="text-[var(--adn-text-muted)]" />
          <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, company" className="flex-1 text-sm border-none focus:outline-none" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="border border-[var(--adn-line)] text-sm px-3 py-2">
          <option value="">All roles</option>
          {[{id:"seller",label:"Seller"},...ROLES,{id:"admin",label:"Admin"}].map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <span className="text-xs text-[var(--adn-text-muted)]">{users.length} user{users.length !== 1 ? "s" : ""}</span>
      </div>
      {busy ? <p className="p-6 text-sm text-[var(--adn-text-soft)]">Loading…</p> :
       users.length === 0 ? <p className="p-6 text-sm text-[var(--adn-text-soft)]">No users.</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--adn-cream)] text-left text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)]">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Approved</th>
                <th className="px-4 py-3">Last IP</th>
                <th className="px-4 py-3">Last seen</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--adn-line)]">
              {users.map((u) => {
                const t = getRoleTheme(u.role);
                return (
                  <tr key={u.user_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name || "—"}</div>
                      <div className="text-xs text-[var(--adn-text-muted)]">{u.email}</div>
                      {u.company && <div className="text-[11px] text-[var(--adn-text-muted)]">{u.company}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <select value={u.role || ""} onChange={(e) => changeRole(u, e.target.value)} className="border border-[var(--adn-line)] text-xs px-2 py-1" style={{ color: t.accentText, backgroundColor: t.accentSoft }} data-testid={`role-select-${u.user_id}`}>
                        {[{id:"seller",label:"Seller"},...ROLES,{id:"admin",label:"Admin"}].map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`toggle-approve-${u.user_id}`} onClick={() => toggleApprove(u)} className={`pill ${u.is_approved ? "pill-accepted" : "pill-pending"}`}>
                        {u.is_approved ? "Yes" : "No"}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {u.last_ip ? (
                        <button data-testid={`view-activity-${u.user_id}`} onClick={() => window.__adminOpenActivity?.(u.user_id)} className="hover:underline" title="View full activity">
                          {u.last_ip}
                        </button>
                      ) : <span className="text-[var(--adn-text-muted)]">never</span>}
                    </td>
                    <td className="px-4 py-3 text-[var(--adn-text-soft)] text-xs">{u.last_seen_at ? fmtDate(u.last_seen_at) : fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => window.__adminOpenActivity?.(u.user_id)} className="text-[var(--adn-text-soft)] hover:bg-slate-100 p-2" title="Activity log">
                          <Activity size={14} />
                        </button>
                        <button data-testid={`delete-user-${u.user_id}`} onClick={() => remove(u)} className="text-rose-600 hover:bg-rose-50 p-2" title="Delete user (cascades)">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function PendingProvidersPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/users", { params: { pending: true } }); setItems(data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (uid, approved) => {
    try { await api.post("/admin/approve-provider", { user_id: uid, approved }); toast.success(approved ? "Approved" : "Rejected"); load(); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-[var(--adn-line)] p-6">
      <p className="eyebrow">Pending approvals ({items.length})</p>
      {items.length === 0 ? <p className="mt-4 text-sm text-[var(--adn-text-soft)]">All caught up — no pending providers.</p> :
        <ul className="mt-4 divide-y divide-[var(--adn-line)]">
          {items.map((p) => (
            <li key={p.user_id} className="py-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-semibold">{p.name} <span className="pill pill-neutral ml-2">{p.role}</span></div>
                <div className="text-xs text-[var(--adn-text-muted)] mt-0.5">{p.email} · {p.company || "no company"} · {p.cities?.join(", ") || "no cities"}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`approve-${p.user_id}`} onClick={() => decide(p.user_id, true)} className="btn-gold px-4 py-2 rounded text-xs inline-flex items-center gap-1"><CheckCircle2 size={14} /> Approve</button>
                <button data-testid={`reject-${p.user_id}`} onClick={() => decide(p.user_id, false)} className="btn-outline-navy px-4 py-2 rounded text-xs inline-flex items-center gap-1"><X size={14} /> Reject</button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

function PropertiesPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/properties"); setItems(data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (pid, addr) => {
    if (!window.confirm(`Delete property ${addr}? Offers on this property will also be deleted.`)) return;
    try { await api.delete(`/admin/properties/${pid}`); toast.success("Deleted"); load(); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-[var(--adn-line)] p-6">
      <p className="eyebrow">All properties ({items.length})</p>
      {items.length === 0 ? <p className="mt-3 text-sm text-[var(--adn-text-soft)]">No properties yet.</p> :
        <ul className="mt-4 divide-y divide-[var(--adn-line)]">
          {items.map((p) => (
            <li key={p.property_id} className="py-3 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium truncate">{p.address_line1}, {p.city}, {p.state} {p.zip_code}</div>
                <div className="text-xs text-[var(--adn-text-muted)]">{fmtDate(p.created_at)} · {(p.situations || []).join(", ") || "no tags"} · status: {p.status}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="pill pill-neutral">{p.status}</span>
                <button data-testid={`delete-property-${p.property_id}`} onClick={() => remove(p.property_id, p.address_line1)} className="text-rose-600 hover:bg-rose-50 p-2">
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

function OffersPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/offers"); setItems(data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (oid) => {
    if (!window.confirm("Delete this offer permanently?")) return;
    try { await api.delete(`/admin/offers/${oid}`); toast.success("Deleted"); load(); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-[var(--adn-line)] p-6">
      <p className="eyebrow">All offers ({items.length})</p>
      {items.length === 0 ? <p className="mt-3 text-sm text-[var(--adn-text-soft)]">No offers yet.</p> :
        <div className="overflow-x-auto">
          <table className="w-full mt-4 text-sm">
            <thead className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)]">
              <tr><th className="py-2">Type</th><th>Provider</th><th>Amount</th><th>Close</th><th>Status</th><th>Date</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-[var(--adn-line)]">
              {items.map((o) => (
                <tr key={o.offer_id}>
                  <td className="py-2">{o.offer_type}</td>
                  <td>{o.provider_name}</td>
                  <td className="tabular">{fmtUSD(o.amount)}</td>
                  <td className="tabular">{o.closing_days ? `${o.closing_days}d` : "—"}</td>
                  <td><span className={`pill ${o.status === "accepted" ? "pill-accepted" : o.status === "rejected" ? "pill-rejected" : "pill-pending"}`}>{o.status}</span></td>
                  <td className="text-[var(--adn-text-soft)] text-xs">{fmtDate(o.created_at)}</td>
                  <td className="text-right">
                    <button data-testid={`delete-offer-${o.offer_id}`} onClick={() => remove(o.offer_id)} className="text-rose-600 hover:bg-rose-50 p-2"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function ConversationsPanel({ onChange }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const loadThreads = useCallback(async () => { try { const { data } = await api.get("/admin/threads"); setThreads(data); } catch {} }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const open = async (t) => {
    setActive(t);
    try { const { data } = await api.get(`/admin/threads/${t.thread_id}/messages`); setMsgs(data); } catch {}
  };
  const remove = async (tid) => {
    if (!window.confirm("Delete this entire conversation? All messages will be removed.")) return;
    try { await api.delete(`/admin/threads/${tid}`); toast.success("Conversation deleted"); setActive(null); setMsgs([]); loadThreads(); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-[var(--adn-line)] grid md:grid-cols-3 min-h-[500px]">
      <div className="border-r border-[var(--adn-line)] max-h-[600px] overflow-y-auto">
        <div className="p-4 border-b border-[var(--adn-line)]">
          <p className="eyebrow">All conversations</p>
          <p className="text-xs text-[var(--adn-text-muted)] mt-1">{threads.length} thread{threads.length !== 1 ? "s" : ""}</p>
        </div>
        {threads.length === 0 && <p className="p-5 text-sm text-[var(--adn-text-soft)]">No conversations yet.</p>}
        {threads.map((t) => (
          <button key={t.thread_id} data-testid={`admin-thread-${t.thread_id}`} onClick={() => open(t)} className={`w-full text-left p-4 border-b border-[var(--adn-line)] hover:bg-slate-50 ${active?.thread_id === t.thread_id ? "bg-slate-50" : ""}`}>
            <div className="text-sm font-medium">
              {(t.participant_info || []).map((p) => p.name).join(" ↔ ")}
            </div>
            <div className="text-xs text-[var(--adn-text-muted)] mt-0.5 flex items-center gap-1">
              <MessageSquare size={11} /> {t.message_count} msg{t.message_count !== 1 ? "s" : ""} · {fmtDate(t.last_at)}
            </div>
            <div className="text-xs text-[var(--adn-text-muted)] truncate mt-1">{t.last_message?.body || "—"}</div>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 flex flex-col">
        {!active ? <div className="flex-1 grid place-items-center text-sm text-[var(--adn-text-soft)] p-8">Select a conversation to view all messages</div> : (
          <>
            <div className="p-4 border-b border-[var(--adn-line)] flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display font-semibold">{(active.participant_info || []).map((p) => `${p.name} (${p.role})`).join(" ↔ ")}</div>
                <div className="text-xs text-[var(--adn-text-muted)] mt-0.5">Thread {active.thread_id}{active.property_id ? ` · property ${active.property_id}` : ""}</div>
              </div>
              <button data-testid={`delete-thread-${active.thread_id}`} onClick={() => remove(active.thread_id)} className="text-rose-600 hover:bg-rose-50 px-3 py-2 rounded inline-flex items-center gap-1 text-xs">
                <Trash2 size={12} /> Delete conversation
              </button>
            </div>
            <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[460px]">
              {msgs.length === 0 ? <p className="text-sm text-[var(--adn-text-soft)]">No messages.</p> :
                msgs.map((m) => (
                  <div key={m.message_id} className="text-sm border-l-2 border-[var(--adn-line)] pl-3">
                    <div className="text-[11px] text-[var(--adn-text-muted)]">{m.sender_name} · {fmtDate(m.created_at)}</div>
                    <div className="mt-1">{m.body}</div>
                  </div>
                ))
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AppointmentsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/appointments"); setItems(data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try { await api.delete(`/admin/appointments/${id}`); toast.success("Deleted"); load(); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-[var(--adn-line)] p-6">
      <p className="eyebrow">All appointments ({items.length})</p>
      {items.length === 0 ? <p className="mt-3 text-sm text-[var(--adn-text-soft)]">No appointments scheduled.</p> :
        <ul className="mt-4 divide-y divide-[var(--adn-line)]">
          {items.map((a) => (
            <li key={a.appointment_id} className="py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium flex items-center gap-2"><Calendar size={14} className="text-[var(--adn-gold)]" /> {fmtDate(a.start_at)} · {a.duration_min}m</div>
                <div className="text-xs text-[var(--adn-text-muted)] mt-0.5">{a.location || "Location TBD"} · {a.notes || ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="pill pill-neutral">{a.status}</span>
                <button data-testid={`delete-appt-${a.appointment_id}`} onClick={() => remove(a.appointment_id)} className="text-rose-600 hover:bg-rose-50 p-2"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

function DangerZonePanel({ onChange }) {
  const [scopes, setScopes] = useState({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (k) => setScopes((s) => ({ ...s, [k]: !s[k] }));
  const ALL_SCOPES = [
    { id: "offers", label: "All offers" },
    { id: "properties", label: "All properties (and their offers)" },
    { id: "messages", label: "All messages" },
    { id: "threads", label: "All conversation threads" },
    { id: "appointments", label: "All appointments" },
    { id: "files", label: "All file metadata (uploads)" },
    { id: "users_nonadmin", label: "All non-admin users (DESTRUCTIVE — keeps you signed in)" },
  ];

  const run = async () => {
    const picked = Object.entries(scopes).filter(([_, v]) => v).map(([k]) => k);
    if (picked.length === 0) { toast.error("Pick at least one scope"); return; }
    if (confirm !== "PURGE") { toast.error("Type PURGE to confirm"); return; }
    if (!window.confirm(`This will permanently delete: ${picked.join(", ")}. Are you ABSOLUTELY sure?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge", { confirm, scopes: picked });
      toast.success("Purged: " + JSON.stringify(data.deleted));
      setScopes({}); setConfirm(""); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-rose-300 bg-rose-50/40 p-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={24} />
        <div>
          <h2 className="font-display text-xl font-semibold text-rose-900">Danger Zone</h2>
          <p className="text-sm text-rose-800/80 mt-1">Permanently delete platform data. There is no undo. Use only for test cleanup or compliance requests.</p>
        </div>
      </div>

      <div className="mt-6 bg-white border border-rose-200 p-5">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-rose-700">Select what to purge</p>
        <ul className="mt-3 space-y-2">
          {ALL_SCOPES.map((s) => (
            <li key={s.id}>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`purge-${s.id}`}
                  checked={!!scopes[s.id]}
                  onChange={() => toggle(s.id)}
                  className="accent-rose-600"
                />
                <span>{s.label}</span>
              </label>
            </li>
          ))}
        </ul>

        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-rose-700">Type <span className="font-mono">PURGE</span> to confirm</p>
          <input
            data-testid="purge-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="PURGE"
            className="mt-2 border-2 border-rose-300 bg-white px-3 py-2 text-sm w-full max-w-xs font-mono focus:outline-none focus:border-rose-600"
          />
        </div>

        <button
          data-testid="purge-run"
          onClick={run}
          disabled={busy || confirm !== "PURGE"}
          className="mt-5 bg-rose-600 text-white px-5 py-3 rounded text-sm font-semibold hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          <Trash2 size={14} /> {busy ? "Purging…" : "Run Purge"}
        </button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, v, accent }) {
  return (
    <div className="bg-white border border-[var(--adn-line)] p-5">
      <Icon size={18} className={accent ? "text-[var(--adn-gold)]" : "text-[var(--adn-text-soft)]"} />
      <div className={`mt-3 font-display text-3xl font-light tabular ${accent ? "text-[var(--adn-gold)]" : ""}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--adn-text-muted)] mt-1">{label}</div>
    </div>
  );
}


function AuditLogPanel() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ action: "", ip: "", user_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.ip) params.ip = filters.ip;
      if (filters.user_id) params.user_id = filters.user_id;
      const { data } = await api.get("/admin/audit-log", { params });
      setItems(data);
    } catch {} finally { setBusy(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-[var(--adn-line)]">
      <div className="p-4 border-b border-[var(--adn-line)] flex flex-wrap items-center gap-3">
        <Globe2 size={14} className="text-[var(--adn-text-muted)]" />
        <input data-testid="audit-action" placeholder="action contains…" value={filters.action} onChange={(e)=>setFilters({...filters, action: e.target.value})} className="border border-[var(--adn-line)] text-xs px-2 py-1.5 min-w-[140px]" />
        <input data-testid="audit-ip" placeholder="IP contains…" value={filters.ip} onChange={(e)=>setFilters({...filters, ip: e.target.value})} className="border border-[var(--adn-line)] text-xs px-2 py-1.5 font-mono min-w-[140px]" />
        <input data-testid="audit-user" placeholder="user_id" value={filters.user_id} onChange={(e)=>setFilters({...filters, user_id: e.target.value})} className="border border-[var(--adn-line)] text-xs px-2 py-1.5 font-mono min-w-[160px]" />
        <span className="text-xs text-[var(--adn-text-muted)] ml-auto">{items.length} entries</span>
      </div>
      {busy ? <p className="p-6 text-sm text-[var(--adn-text-soft)]">Loading…</p> :
        items.length === 0 ? <p className="p-6 text-sm text-[var(--adn-text-soft)]">No activity matches filters.</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--adn-cream)] text-left text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)]">
              <tr>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Meta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--adn-line)]">
              {items.map((it) => (
                <tr key={it.log_id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-xs text-[var(--adn-text-soft)] whitespace-nowrap">{fmtDate(it.at)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{it.action}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {it.user ? (
                      <button onClick={() => window.__adminOpenActivity?.(it.user_id)} className="hover:underline">
                        <div className="font-medium">{it.user.email}</div>
                        <div className="text-[10px] text-[var(--adn-text-muted)]">{it.user.role}</div>
                      </button>
                    ) : <span className="text-[var(--adn-text-muted)]">anonymous</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{it.ip || "—"}</td>
                  <td className="px-4 py-2.5 text-[11px] text-[var(--adn-text-soft)] max-w-[260px] truncate">
                    {it.meta && Object.keys(it.meta).length > 0 ? JSON.stringify(it.meta) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function UserActivityModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/admin/users/${userId}/activity`); setData(data); } catch {}
    })();
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-[var(--adn-navy)]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[var(--adn-line)] max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-[var(--adn-line)] flex items-center justify-between">
          <div>
            <p className="eyebrow">User activity</p>
            {data?.user && (
              <div className="mt-1">
                <div className="font-display font-semibold">{data.user.name || data.user.email}</div>
                <div className="text-xs text-[var(--adn-text-muted)]">{data.user.email} · {data.user.role}</div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {data?.user && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat k="Last IP" v={data.user.last_ip || "—"} mono />
              <Stat k="Last seen" v={data.user.last_seen_at ? fmtDate(data.user.last_seen_at) : "—"} />
              <Stat k="Last login" v={data.user.last_login ? fmtDate(data.user.last_login) : "—"} />
              <Stat k="Joined" v={data.user.created_at ? fmtDate(data.user.created_at) : "—"} />
              <Stat k="User-Agent" v={data.user.last_user_agent || "—"} full small />
            </div>
          )}

          <div>
            <p className="eyebrow">IP history (last 50)</p>
            {(data?.ip_history || []).length === 0 ? <p className="text-sm text-[var(--adn-text-soft)] mt-2">No IP records yet.</p> :
              <ul className="mt-3 divide-y divide-[var(--adn-line)] border border-[var(--adn-line)]">
                {[...(data.ip_history || [])].reverse().map((h, i) => (
                  <li key={i} className="px-3 py-2 flex items-center justify-between text-xs">
                    <span className="font-mono">{h.ip}</span>
                    <span className="text-[var(--adn-text-muted)]">{h.action}</span>
                    <span className="text-[var(--adn-text-muted)]">{fmtDate(h.at)}</span>
                  </li>
                ))}
              </ul>
            }
          </div>

          <div>
            <p className="eyebrow">Recent activity</p>
            {(data?.activity || []).length === 0 ? <p className="text-sm text-[var(--adn-text-soft)] mt-2">No activity yet.</p> :
              <table className="w-full mt-3 text-xs border border-[var(--adn-line)]">
                <thead className="bg-[var(--adn-cream)] text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)]">
                  <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">IP</th><th className="px-3 py-2 text-left">Meta</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--adn-line)]">
                  {data.activity.map((a) => (
                    <tr key={a.log_id}>
                      <td className="px-3 py-2 whitespace-nowrap text-[var(--adn-text-soft)]">{fmtDate(a.at)}</td>
                      <td className="px-3 py-2 font-mono">{a.action}</td>
                      <td className="px-3 py-2 font-mono">{a.ip || "—"}</td>
                      <td className="px-3 py-2 text-[var(--adn-text-soft)] max-w-[300px] truncate">
                        {a.meta && Object.keys(a.meta).length > 0 ? JSON.stringify(a.meta) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v, mono, full, small }) {
  return (
    <div className={`border border-[var(--adn-line)] p-3 ${full ? "col-span-full" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)] font-bold">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono" : ""} ${small ? "text-xs break-all" : "text-sm"}`}>{v}</div>
    </div>
  );
}
